import * as React from "react";
import { ChevronDown, Layers, Palette, SlidersHorizontal, Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";
import type { TokenGroup } from "@/data/config";
import { usePortal } from "@/features/portal/portal-context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TokenControl } from "@/features/theming/token-control";
import { ColorFromUrl } from "@/features/theming/color-from-url";

const GROUP_ICON: Record<TokenGroup, typeof Sparkles> = {
  Brand: Sparkles,
  Surfaces: Layers,
  Actions: Palette,
};

/**
 * "Advanced" — the original per-token grid, demoted from the primary flow to a
 * collapsed drawer (capability kept, UI demoted). Also home to the raw-hex URL
 * ingest for power users; the primary flow generates full treatments instead.
 * Editing any token here detaches the draft from the generated recipe (the studio
 * marks it custom), so raw edits still preview + publish exactly as before.
 */
export function AdvancedTokens() {
  const { draft, onThemeChange, defs, selectedThemeId } = usePortal();
  const [open, setOpen] = React.useState(false);
  const groups = [...new Set(defs.map((d) => d.group))];

  return (
    <div className="mt-6 rounded-[12px] border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 rounded-[12px] px-4 py-3 text-left outline-none transition-colors hover:bg-panel-2/40 focus-visible:ring-[3px] focus-visible:ring-ring"
      >
        <SlidersHorizontal className="size-4 text-muted-foreground" />
        <div className="flex-1">
          <div className="text-[13px] font-semibold">Advanced — edit individual tokens</div>
          <div className="text-[12px] text-text-3">
            Fine-tune each colour by hand, or generate a raw palette from a URL.
          </div>
        </div>
        <ChevronDown className={cn("size-4 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="border-t border-border p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {groups.map((g) => {
              const GroupIcon = GROUP_ICON[g];
              const isBrand = g === "Brand";
              return (
                <Card key={g} className={cn(isBrand && "sm:col-span-2")}>
                  <CardHeader>
                    <CardTitle>
                      <GroupIcon /> {g}
                    </CardTitle>
                  </CardHeader>
                  <CardContent
                    className={cn(
                      "gap-5 pt-4",
                      isBrand ? "grid grid-cols-1 sm:grid-cols-2" : "flex flex-col",
                    )}
                  >
                    {isBrand && (
                      <div className="sm:col-span-2">
                        <ColorFromUrl key={selectedThemeId} />
                      </div>
                    )}
                    {defs
                      .filter((d) => d.group === g)
                      .map((d) => (
                        <TokenControl key={d.id} def={d} theme={draft.theme} onChange={onThemeChange} />
                      ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
